/**
 * The insurance verification request types — ONE vocabulary.
 *
 * Every surface that used to branch on `requestType === "estimate"` (with the
 * `else` silently meaning "breakdown") reads from here instead. That default
 * was the hazard: a third value fell into the breakdown branch everywhere — it
 * skipped the submissions step, dropped its `estimates` from the POST, opened
 * the wrong modal tab and labelled itself "Insurance Breakdown". Keeping the
 * table here means a new type is added in one place and nothing else guesses.
 *
 * Mirrors the backend's `InsuranceVerificationRequest.REQUEST_TYPE_CHOICES`
 * (clinics/models.py); the tag names mirror the seeded StagingGroups
 * (clinics/signals.py `create_default_staging_groups`). Change one, change
 * the other.
 */

export const DEFAULT_REQUEST_TYPE = "breakdown";
// The patient tag rolling up a policy's "Details Needed by Smilepass" flag (and items
// waiting on the office for missing information). Was "Need the Missing Info", before that "Needs Attention".
export const MISSING_INFO_TAG_NAME = "Details Needed by Smilepass";

export const REQUEST_TYPES = {
  breakdown: {
    value: "breakdown",
    cardTitle: "Insurance Breakdown",
    rowLabel: "Insurance Breakdown",            // requests table + composer review line
    filterLabel: "Breakdowns",                  // the requests page's type filter
    noun: "Breakdown",                          // VerificationActionButton → "Request Breakdown"
    successToast: "Breakdown requested.",
    viewAction: "View coverage",                // requests table, Ready rows
    viewTab: "breakdown",                       // InsuranceMasterModal tab key
    needsSubmissions: false,
    submissionsStepTitle: null,
    submissionNoun: null,
    submissionNounPlural: null,
    submissionNounWithArticle: null,
    requestedTagNames: ["Breakdown - Requested", "Breakdown - Requested - Urgent"],
    doneTagName: "Breakdown - Verified",
    successField: "breakdown_verified_at",      // the per-policy outcome timestamp
    successLabel: "Mark Verified",
    deleteNote:
      "Any coverage already returned stays on the patient, and so does the office's billing record.",
  },
  estimate: {
    value: "estimate",
    // The wizard's tile only (cardTitle is read nowhere else); every other
    // label keeps "Insurance Estimate".
    cardTitle: "Estimate/Predetermination Tracking",
    rowLabel: "Insurance Estimate",
    filterLabel: "Estimates",
    noun: "Estimate",
    successToast: "Estimate requested.",
    viewAction: "View estimate",
    viewTab: "estimates",
    needsSubmissions: true,
    submissionsStepTitle: "Estimates",
    submissionNoun: "estimate",
    submissionNounPlural: "estimates",
    submissionNounWithArticle: "an estimate",
    requestedTagNames: ["Estimate - Requested", "Estimate - Requested - Urgent"],
    doneTagName: "New Estimate Available",
    successField: "new_estimate_available_at",
    successLabel: "Mark Estimate Available",
    deleteNote:
      "Any coverage or estimate already returned stays on the patient, and so does the " +
      "office's billing record. The treatment details attached to this request are removed with it.",
  },
  claim_followup: {
    value: "claim_followup",
    cardTitle: "Claim Follow\u2011up",           // non-breaking hyphen: the card wraps "Claim / Follow-up", never "Follow- / up"
    rowLabel: "Claim Follow-up",
    filterLabel: "Claims",
    noun: "Claim Follow-up",
    successToast: "Claim follow-up requested.",
    viewAction: "View follow-up",               // the type is already in the Request column
    viewTab: "claims",
    needsSubmissions: true,
    submissionsStepTitle: "Claims",
    submissionNoun: "claim",
    submissionNounPlural: "claims",
    submissionNounWithArticle: "a claim",
    requestedTagNames: ["Claim Follow-up - Requested", "Claim Follow-up - Requested - Urgent"],
    doneTagName: "Claim Follow-up - Done",
    successField: "claim_followup_done_at",
    successLabel: "Mark Follow-up Done",
    deleteNote:
      "The office's billing record stays. The claim details attached to this request are removed with it.",
  },
};

/** Display order — the wizard's cards and any type filter. */
export const REQUEST_TYPE_ORDER = ["breakdown", "estimate", "claim_followup"];

/** The entry for a type; an unknown value reads as the default so nothing crashes. */
export function requestTypeMeta(type) {
  return REQUEST_TYPES[type] || REQUEST_TYPES[DEFAULT_REQUEST_TYPE];
}

/** Does this type's request have to NAME the submissions being chased? */
export function needsSubmissions(type) {
  return !!requestTypeMeta(type).needsSubmissions;
}

/** The insurance-modal tab that shows this request's work. */
export function tabForRequestType(type) {
  return requestTypeMeta(type).viewTab;
}

/** The insurance modal's tabs, and the clamp every tab-carrying entry point uses. */
export const INSURANCE_TABS = ["breakdown", "estimates", "claims"];

export function normalizeInsuranceTab(tab) {
  return INSURANCE_TABS.indexOf(tab) === -1 ? "breakdown" : tab;
}

/**
 * { [doneTagName]: [requested, urgent, "Details Needed by Smilepass"] } — the request
 * tags an outcome clears. OutcomeTagControl's no-policy fallback map.
 */
export const REQUEST_TAGS_CLEARED_BY_DONE = REQUEST_TYPE_ORDER.reduce(function (map, key) {
  const t = REQUEST_TYPES[key];
  map[t.doneTagName] = t.requestedTagNames.concat([MISSING_INFO_TAG_NAME]);
  return map;
}, {});
